'use client'

import * as React from 'react'

import { cn } from '../../../lib/utils'
import s from '../../styles/animations.module.css'
import MetafieldLabel from '../MetafieldLabel'
import { FieldChange } from '../FieldChange'

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  errorMessage?: string
  helpText?: string
  height?: number
  id: string
  textAlignment?: 'left' | 'center' | 'right'
  value?: string
  originalValue?: string | null
  isSkeleton?: boolean
  isDisabled?: boolean
  placeholder?: string
  handleChange?: (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => void | Promise<void>
  metafield?: {
    label: string
    href: string
  }
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      value,
      originalValue,
      handleChange,
      height,
      isSkeleton,
      placeholder,
      errorMessage,
      helpText,
      ...props
    },
    ref,
  ) => {
    const { isDisabled, ...textareaProps } = props
    const [inputValue, setInputValue] = React.useState<string>(value || '')
    const [lastChange, setLastChange] = React.useState<string>(value || '')

    let timer: NodeJS.Timeout

    // Helper function to normalize values for comparison
    const normalizeValue = (val: string | null | undefined): string => {
      if (val === null || val === undefined || val === '') {
        return '( None )'
      }
      return String(val)
    }

    // Check if we should show FieldChange component
    const shouldShowFieldChange =
      originalValue !== undefined &&
      normalizeValue(originalValue) !== normalizeValue(value)

    const hasChangedFromOutside = (value: string | number | undefined) => {
      return (
        lastChange == '' ||
        (lastChange == inputValue && inputValue != value && value !== undefined)
      )
    }

    // first render
    React.useEffect(() => {
      if (!inputValue && value) {
        setLastChange(value)
        return setInputValue(value)
      }

      // if value changes from outside, update input value
      if (hasChangedFromOutside(value) && value !== undefined) {
        setLastChange(value)
        return setInputValue(value)
      }
    }, [value])

    // debounce function calling timeout
    const debounce = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        clearTimeout(timer)
        setInputValue(e.target.value)
        timer = setTimeout(() => {
          // Call triggerChange without awaiting to ensure no Promise is returned
          void triggerChange(e)
          setLastChange(e.target.value)
        }, 500)
      },
      [],
    )

    const triggerChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      // dispatch if exists
      handleChange && (await handleChange(e))
    }

    return (
      <div className="bg-transparent w-full">
        <label
          className={cn({
            'text-destructive opacity-100 leading-tight w-full': errorMessage,
          })}
        >
          {props.label && (
            <div
              className={cn(
                'flex justify-between items-center text-muted-foreground mb-1 h-5',
                className,
              )}
            >
              {props.label}
              <div className="flex items-center gap-2">
                {shouldShowFieldChange && (
                  <FieldChange
                    oldValue={normalizeValue(originalValue)}
                    newValue={normalizeValue(value)}
                    size="sm"
                    handleRevert={() => {
                      if (handleChange) {
                        const syntheticEvent = {
                          target: { value: originalValue || '' },
                        } as React.ChangeEvent<HTMLTextAreaElement>
                        handleChange(syntheticEvent)
                      }
                    }}
                  />
                )}
                {props.metafield && <MetafieldLabel {...props.metafield} />}
              </div>
            </div>
          )}

          <textarea
            title={`text-area-${props.id}`}
            className={cn(
              'flex w-full min-h-[60px] rounded-md px-3 py-2 text-sm shadow-sm focus-within:outline-none focus-within:ring-1 focus-within:ring-ring',
              {
                'bg-secondary animate-pulse placeholder:text-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed':
                  isSkeleton,
              },
              {
                'bg-transparent border border-input placeholder:text-muted-foreground disabled:opacity-50':
                  !isSkeleton,
              },
              {
                [`h-[${height}px]`]: height,
              },
              className,
            )}
            ref={ref}
            value={inputValue}
            onChange={debounce}
            disabled={isSkeleton || isDisabled}
            placeholder={placeholder}
            aria-errormessage={errorMessage}
            {...textareaProps}
          />
        </label>
        {helpText && (
          <div className="text-muted-foreground normal-case text-xs mt-2">
            {helpText}
          </div>
        )}
        {errorMessage && (
          <div
            className={cn('text-destructive text-xs mt-2', s.smallTransitionIn)}
          >
            {errorMessage}
          </div>
        )}
      </div>
    )
  },
)
Textarea.displayName = 'Textarea'

export { Textarea }
